import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import VideoLabelIcon from "@material-ui/icons/VideoLabel";

import PmcData from "./PmcInfo";
import BuildingInfo from "./buildingInfo";
import { api } from "../../../../services";
import { COLORS } from "../../../../theme/colors";
import endPoints from "../../../../services/apiEndPoints";
import { OnboardedDoneModal } from "./OnboardedDoneModal";
import { validateObject } from "../../../../services/utils";
import { MAX_STEP_TENANCY, TenancyData } from "./tenancyData";
import { TextButton, Button } from "../../../../components/base";
import { updateOnboardingStatus, VALIDATION_ERROR_MESSAGE } from "../flows";
import { CENTRAL, ONBOARDING_STATUES } from "../../../../services/constants";
import { ElectricityData, MAX_STEP_ELECTRICITY, UPLOAD_BILL_FILES_STEP } from "./electricityData";

const QontoConnector = withStyles({
  alternativeLabel: {
    top: 10,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  active: {
    "& $line": {
      borderColor: `${COLORS.darks[0][6]}`,
    },
  },
  completed: {
    "& $line": {
      borderColor: `${COLORS.darks[0][6]}`,
    },
  },
  line: {
    borderColor: COLORS.darks[0][6],
    borderTopWidth: 3,
    borderRadius: 1,
  },
})(StepConnector);

const useQontoStepIconStyles = makeStyles({
  root: {
    color: COLORS.darks[0][25],
    display: "flex",
    height: 22,
    alignItems: "center",
    padding: "0px 7px",
    borderRadius: "30px",
    backgroundColor: COLORS.white,
  },
  active: {
    color: COLORS.purple,
    border: `2px solid ${COLORS.gray[1]}`,
  },
  circle: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  completed: {
    color: COLORS.blueMagenta,
    zIndex: 1,
    fontSize: 18,
  },
});

function QontoStepIcon(props) {
  const classes = useQontoStepIconStyles();
  const { active } = props;

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
      })}
    >
      <div className={classes.circle} />
    </div>
  );
}

QontoStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

const useColorlibStepIconStyles = makeStyles({
  root: {
    backgroundColor: COLORS.silver,
    zIndex: 1,
    color: COLORS.white,
    width: 50,
    height: 50,
    display: "flex",
    borderRadius: "50%",
    justifyContent: "center",
    alignItems: "center",
  },
  active: {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
    boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
  },
  completed: {
    backgroundImage:
      "linear-gradient( 136deg, rgb(242,113,33) 0%, rgb(233,64,87) 50%, rgb(138,35,135) 100%)",
  },
});

function ColorlibStepIcon(props) {
  const classes = useColorlibStepIconStyles();
  const { active, completed } = props;

  const icons = {
    1: <SettingsIcon />,
    2: <GroupAddIcon />,
    3: <VideoLabelIcon />,
  };

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
        [classes.completed]: completed,
      })}
    >
      {icons[String(props.icon)]}
    </div>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  button: {
    marginRight: theme.spacing(1),
  },
  buttonBox: {
    marginTop: 40,
    display: "flex",
    justifyContent: "space-between",
  },
  rightButton: {
    display: "flex",
  },
  label: {
    color: COLORS.purples[0],
    "& .MuiStepLabel-label.MuiStepLabel-active": {
      color: COLORS.purples[0],
    },
  },
  stepDiv: {
    background: COLORS.darks[0][3],
  },
  stepperDiv: {
    width: 600,
    background: COLORS.darks[0][1],
    "&.MuiStepper-root": {
      padding: "24px 0px 0px",
    },
  },
  step: {
    width: 860,
    padding: "46px 26px",
    background: COLORS.darks[0][3],
  },
}));

export const CLICKING = {
  none: 0,
  save: 1,
  next: 2,
};

const ELECTRICITY_DATA_STEP = 2;
const TENANCY_DATA_STEP = 3;

export const BOPSteppers = ({ building, setLoading, onError, onClose }) => {
  const classes = useStyles();

  const [activeStep, setActiveStep] = useState(0);
  const [electricityStep, setElectricityStep] = useState(0);
  const [tenancyStep, setTenancyStep] = useState(0);
  const [clicking, setClicking] = useState(CLICKING.none);
  const [skippedTenantStepInElectricity, setSkippedTenantStepInElectricity] = useState(false);
  const [unitsFileUploaded, setUnitsFileUploaded] = useState(false);
  const [openLastPopup, setOpenLastPopup] = useState(false);

  const onElectricityStep = activeStep === ELECTRICITY_DATA_STEP;
  const onTenancyStep = activeStep === TENANCY_DATA_STEP;
  const steps = ["Building info", "PMC info", "Electricity Data", "Tenancy & Units Data"];
  const StepContent = [BuildingInfo, PmcData, ElectricityData, TenancyData][activeStep];
  let element = document.getElementById("bop-container");

  useEffect(() => {
    const obStatus = building?.onboarding_status;
    if (obStatus === ONBOARDING_STATUES.electricity_data) {
      setActiveStep(ELECTRICITY_DATA_STEP);
    } else if (obStatus === ONBOARDING_STATUES.tenant_and_unit_data) {
      setActiveStep(TENANCY_DATA_STEP);
    }
  }, [building?.onboarding_status]);

  useEffect(() => {
    if (element) {
      element.scrollTo(0, 0);
    }
  }, [activeStep, element]);

  const handleNext = () => {
    if (onTenancyStep && tenancyStep === MAX_STEP_TENANCY) {
      setOpenLastPopup(true);
      return;
    }
    if (clicking === CLICKING.save) {
      onClose();
      return;
    }
    if (onElectricityStep && electricityStep < MAX_STEP_ELECTRICITY) {
      setElectricityStep(electricityStep + 1);
    } else if (onTenancyStep && tenancyStep < MAX_STEP_TENANCY) {
      setTenancyStep(tenancyStep + 1);
    } else if (activeStep < TENANCY_DATA_STEP) {
      setActiveStep(activeStep + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (onTenancyStep && tenancyStep > 0) {
      setTenancyStep(tenancyStep - 1);
    } else if (onElectricityStep && electricityStep > 0) {
      setElectricityStep(electricityStep - 1);
    } else if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const skipElectricityStep = () => {
    if (skippedTenantStepInElectricity) {
      setElectricityStep(electricityStep - 1);
      setSkippedTenantStepInElectricity(false);
    } else {
      setElectricityStep(electricityStep + 1);
      setSkippedTenantStepInElectricity(true);
    }
  };

  const saveAndContinue = () => {
    if (onTenancyStep && tenancyStep === 0) {
      onClose();
      return;
    }
    setClicking(CLICKING.save);
  };

  const validateSavedElectricityData = async () => {
    const res = await api.get(endPoints.OB_OPERATOR_ELECTRICITY_DATA.replace("{id}", building.id));
    const data = res.data;
    const meterTypeValidationFields = ["electricity_bill", "type", "public_meters"];
    if (
      data.meter_type?.public_meters_owner === "TENANT" &&
      data.meter_type?.unit_meters_owner === "TENANT"
    )
      meterTypeValidationFields.splice(0, 1);

    return (
      validateObject(data, [
        { meter_type: meterTypeValidationFields },
        "tenant_public_area",
        "tenant_vacant_area",
      ]) &&
      (data.meter_type.type === CENTRAL || validateObject(data.meter_type, ["unit_meters"]))
    );
  };

  return (
    <div className={classes.root}>
      <Box className={classes.stepDiv}>
        {(activeStep !== ELECTRICITY_DATA_STEP || electricityStep !== MAX_STEP_ELECTRICITY) && (
          <Stepper
            className={classes.stepperDiv}
            alternativeLabel
            activeStep={activeStep}
            connector={<QontoConnector />}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={QontoStepIcon} className={classes.label}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Box>
      <div>
        {activeStep === steps.length ? (
          <div>Done</div>
        ) : (
          <div>
            <Box className={classes.step}>
              <StepContent
                building={building}
                handleNext={handleNext}
                clicking={clicking}
                setClicking={setClicking}
                setLoading={setLoading}
                onError={onError}
                electricityStep={electricityStep}
                skipElectricityStep={skipElectricityStep}
                tenancyStep={tenancyStep}
                setUnitsFileUploaded={setUnitsFileUploaded}
              />
            </Box>
            <Box className={classes.buttonBox}>
              <Box className={classes.leftButton}>
                {activeStep > 0 && (
                  <TextButton text="PREV" onClick={handleBack} style={{ margin: 10 }} />
                )}
              </Box>
              <Box className={classes.rightButton}>
                <TextButton
                  text="SAVE AND CONTINUE LATER"
                  style={{ margin: "10px 40px" }}
                  onClick={saveAndContinue}
                />
                <Button
                  text={activeStep === TENANCY_DATA_STEP && tenancyStep === 0 ? "FINISH" : "NEXT"}
                  disabled={activeStep === TENANCY_DATA_STEP && !unitsFileUploaded}
                  onClick={async () => {
                    if (
                      activeStep < ELECTRICITY_DATA_STEP ||
                      (onElectricityStep && electricityStep < UPLOAD_BILL_FILES_STEP) ||
                      (onTenancyStep && tenancyStep <= MAX_STEP_TENANCY && tenancyStep > 0)
                    ) {
                      setClicking(CLICKING.next);
                      return;
                    }

                    if (onElectricityStep) {
                      if (electricityStep === MAX_STEP_ELECTRICITY) {
                        setActiveStep(activeStep + 1);
                        return;
                      }
                      if (
                        electricityStep === MAX_STEP_ELECTRICITY - 1 &&
                        (await validateSavedElectricityData())
                      ) {
                        updateOnboardingStatus(
                          building.id,
                          ONBOARDING_STATUES.tenant_and_unit_data
                        );
                      } else {
                        onError(null, VALIDATION_ERROR_MESSAGE);
                        return;
                      }
                      setElectricityStep(electricityStep + 1);
                      return;
                    }

                    setTenancyStep(tenancyStep + 1);
                  }}
                />
              </Box>
            </Box>
          </div>
        )}
      </div>

      <OnboardedDoneModal
        open={openLastPopup}
        onClose={() => {
          setOpenLastPopup(false);
          onClose();
        }}
      />
    </div>
  );
};
